/**
 * Months of the year enumeration.
 */
export enum Months {
  JANUARY,
  FEBRUARY,
  MARCH,
  APRIL,
  MAY,
  JUNE,
  JULY,
  AUGUST,
  SEPTEMBER,
  OCTOBER,
  NOVEMBER,
  DECEMBER,
}

/**
 * Days of the week enumeration.
 */
export enum Days {
  SUNDAY,
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
}

/**
 * Represents a day with an optional date.
 */
export type Day = {
  date?: Date;
};

/**
 * Represents a week with its number and days.
 */
export type Week = {
  number: number;
  days: Day[];
};

/**
 * Represents a month with its weeks.
 */
export type Month = {
  weeks: Week[];
};

/**
 * Represents the calendar view, which is an array of months.
 */
export type CalendarView = Month[];

/**
 * Props for the useCalendar hook.
 */
export type UseCalendarProps = {
  /**
   * The years available to select from.
   */
  availableYears: number[];

  /**
   * What day a week starts on within the calendar matrix.
   */
  weekStartsOn?: Days;

  /**
   * The initial viewing date.
   */
  viewing?: Date;

  /**
   * The initial date(s) selection.
   */
  selected?: Date[];

  /**
   * The number of months in the calendar.
   */
  numberOfMonths?: number;

  /**
   * The minimum date supported.
   */
  minDate?: Date;

  /**
   * The maximum date supported.
   */
  maxDate?: Date;

  /**
   * The start date of the range.
   */
  startDate?: Date;

  /**
   * The end date of the range.
   */
  endDate?: Date;
};

/**
 * Return value of the useCalendar hook.
 */
export type UseCalendarReturn = {
  /**
   * Returns a copy of the given date with the time set to 00:00:00:00.
   */
  clearTime: (date: Date) => Date;

  /**
   * Returns whether or not a date is between two other dates (inclusive).
   */
  inRange: (date: Date, min?: Date, max?: Date) => boolean;

  /**
   * The date represented in the calendar matrix. Only the month and year parts are used.
   */
  viewing: Date;

  /**
   * Sets the date represented in the calendar matrix. Only the month and year parts are used.
   */
  setViewing: React.Dispatch<React.SetStateAction<Date>>;

  /**
   * Sets the viewing date to today.
   */
  viewToday: () => void;

  /**
   * Sets the viewing date to the specified month.
   */
  viewMonth: (month: Months) => void;

  /**
   * Sets the viewing date to the month before the current month.
   */
  viewPreviousMonth: () => void;

  /**
   * Sets the viewing date to the month after the current month.
   */
  viewNextMonth: () => void;

  /**
   * Sets the viewing date to the specified year.
   */
  viewYear: (year: number) => void;

  /**
   * Sets the viewing date to the year before the current year.
   */
  viewPreviousYear: () => void;

  /**
   * Sets the viewing date to the year after the current year.
   */
  viewNextYear: () => void;

  /**
   * The currently selected dates.
   */
  selected: Date[];

  /**
   * Sets the selected dates, overriding the current selection.
   */
  setSelected: React.Dispatch<React.SetStateAction<Date[]>>;

  /**
   * Resets the selected dates to an empty array.
   */
  clearSelected: () => void;

  /**
   * Checks if a date is selected.
   */
  isSelected: (date: Date[]) => boolean;

  /**
   * Selects one or more dates.
   */
  select: (date: Date[], replaceExisting?: boolean) => void;

  /**
   * Deselects one or more dates.
   */
  deselect: (date: Date[]) => void;

  /**
   * Toggles the selection of a date.
   */
  toggle: (dates: Date[], onSelected?: (dates: Date[]) => void, replaceExisting?: boolean) => void;

  /**
   * Selects a range of dates (inclusive).
   */
  selectRange: (start: Date, end: Date, replaceExisting?: boolean) => void;

  /**
   * Deselects a range of dates (inclusive).
   */
  deselectRange: (start: Date, end: Date) => void;

  /**
   * Retrieves the months in the year view.
   */
  months: () => Date[];

  /**
   * Retrieves the available years.
   */
  getAvailableYears: () => Date[];

  /**
   * The current calendar view, represented as a matrix of days and weeks.
   */
  currentView: CalendarView;
};
